import React, { useState, useEffect } from "react";
import {
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/solid";

const ScoreBoxForWritting = ({ originalWords, practiceItemId, onIsAttempted }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await fetch(
          `/api/get-user-responses/${practiceItemId}`
        );
        if (!response.ok) throw new Error("Failed to fetch responses");

        const responseData = await response.json();
        setResponses(responseData);
        setLoading(false);

        if (responseData.length > 0) {
          onIsAttempted(responseData[0].isAttempted);
        }
      } catch (error) {
        console.error("Error fetching responses:", error);
        setLoading(false);
      }
    };

    fetchResponses();
  }, [onIsAttempted]);

  const toggleBox = () => setIsOpen(!isOpen);

  const deleteResponse = (id) => {
    const updatedResponses = responses.filter((response) => response.id !== id);
    setResponses(updatedResponses);
  };

  const highlightMatchingWords = (originalWords, userResponse) => {
    const words1 = originalWords.split(" ");
    const words2 = userResponse.split(" ");

    return words2.map((word2, index) => (
      <span
        key={index}
        className={`px-1 rounded-md mr-1 mb-1 text-xs md:text-sm ${
          words1.includes(word2) ? "bg-green-300" : "bg-red-400 text-white"
        }`}
      >
        {word2}
      </span>
    ));
  };

  return (
    <div className="p-3 sm:p-4 m-2 sm:m-4 border rounded-lg shadow-lg bg-white">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm sm:text-lg font-bold">Your Responses</h2>
        <button
          onClick={toggleBox}
          className="flex items-center text-gray-500 focus:outline-none"
        >
          {isOpen ? (
            <ChevronUpIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 sm:h-5 sm:w-5" />
          )}
        </button>
      </div>

      {isOpen && (
        <div>
          {responses.map((response, index) => (
            <div
              key={response.id}
              className={`mb-4 sm:mb-6 ${
                index !== responses.length - 1
                  ? "pb-4 sm:pb-6 border-b border-gray-300"
                  : ""
              }`}
            >
              <div className="flex items-start sm:items-center mb-2">
                <p className="font-bold text-xs sm:text-sm mr-2">
                  {index + 1}.
                </p>
                <div className="flex-grow w-full">
                  {/* Responsive Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-300 rounded-lg text-xs sm:text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 sm:px-4 sm:py-3 text-left text-gray-600 whitespace-nowrap">
                            Date/Time
                          </th>
                          <th className="px-2 py-1 sm:px-4 sm:py-3 text-left text-gray-600 whitespace-nowrap">
                            Content
                          </th>
                          <th className="px-2 py-1 sm:px-4 sm:py-3 text-left text-gray-600 whitespace-nowrap">
                            Grammar
                          </th>
                          <th className="px-2 py-1 sm:px-4 sm:py-3 text-left text-gray-600 whitespace-nowrap">
                            Vocabulary
                          </th>
                          <th className="px-2 py-1 sm:px-4 sm:py-3 text-left text-gray-600 whitespace-nowrap">
                            Total
                          </th>
                          <th className="px-2 py-1 sm:px-4 sm:py-3 text-left text-gray-600 whitespace-nowrap">
                            Remarks
                          </th>
                          <th className="relative px-2 py-1 sm:px-4 sm:py-3"></th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-2 py-1 sm:px-4 sm:py-3 text-gray-500">
                            {new Date(response.updatedAt).toLocaleDateString(
                              "en-GB"
                            )}
                          </td>
                          <td className="px-2 py-1 sm:px-4 sm:py-3 text-gray-500">
                            {response.contentScore}
                          </td>
                          <td className="px-2 py-1 sm:px-4 sm:py-3 text-gray-500">
                            {response.grammarScore}
                          </td>
                          <td className="px-2 py-1 sm:px-4 sm:py-3 text-gray-500">
                            {response.vocabularyScore}
                          </td>
                          <td className="px-2 py-1 sm:px-4 sm:py-3 text-gray-500">
                            {response.totalScore}
                          </td>
                          <td className="px-2 py-1 sm:px-4 sm:py-3 text-gray-500">
                            {response.remarks}
                          </td>
                          <td className="px-2 py-1 sm:px-4 sm:py-3 text-right">
                            <TrashIcon
                              className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 cursor-pointer"
                              onClick={() => deleteResponse(response.id)}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="border border-gray-300 rounded-lg p-2 sm:p-3 mt-2 sm:mt-3 text-xs sm:text-sm">
                    <p className="font-bold">User Response:</p>
                    <div className="flex flex-wrap mt-2">
                      {highlightMatchingWords(
                        originalWords,
                        response.userResponse || "No detailed response yet."
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {responses.length === 0 && !loading && (
            <p className="text-center text-gray-500 text-xs sm:text-sm mt-2">
              No responses yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ScoreBoxForWritting;
